import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  useCreateManagerMutation,
  useManagersQuery,
  usePermissionCatalogQuery,
  useRemoveManagerMutation,
  useUpdateManagerMutation,
} from '../../../services/accessControlApi';
import { useSalonAccess } from '../../../context/SalonAccessContext';
import ManagerAccessForm from './ManagerAccessForm';
import createSalooneAccessTheme from './salooneAccessTheme';

const grantPayload = (grant, isActive = grant.is_active !== false) => ({
  salon_id: Number(grant.salon_id),
  is_full_access: Boolean(grant.is_full_access),
  is_active: isActive,
  permission_codes: grant.permission_codes || [],
});

const ManagerAccessPageContent = () => {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback, options = {}) => {
    const translated = t(key, { defaultValue: fallback, ...options });
    return typeof translated === 'string' ? translated : fallback;
  }, [t]);
  const { access, salons, isLoading: accessLoading, hasNoAuthorizedSalon } = useSalonAccess();
  const managersQuery = useManagersQuery();
  const catalogQuery = usePermissionCatalogQuery();
  const [createManager, createState] = useCreateManagerMutation();
  const [updateManager, updateState] = useUpdateManagerMutation();
  const [removeManager, removeState] = useRemoveManagerMutation();
  const [formOpen, setFormOpen] = useState(false);
  const [editingManager, setEditingManager] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [formError, setFormError] = useState('');
  const [pageMessage, setPageMessage] = useState(null);

  const managers = managersQuery.data?.results || [];
  const eligibleEmployees = managersQuery.data?.eligible_employees || [];
  const groups = catalogQuery.data?.groups || [];
  const canGrantManagerManage = access?.role === 'professional_responsable';
  const canManage = canGrantManagerManage || salons.some((salon) =>
    salon.is_owner || salon.permission_codes?.includes('managers.manage'));
  const isBusy = createState.isLoading || updateState.isLoading || removeState.isLoading;

  const permissionLabels = Object.fromEntries(groups.flatMap((group) =>
    (group.permissions || []).map((permission) => [
      permission.code,
      tr(permission.label_key, permission.code),
    ])));

  const friendlyError = (error) => {
    if (error?.status === 409) {
      return tr(
        'pro_manager_access.errors.conflict',
        'Cette adresse appartient déjà à un compte qui ne peut pas être converti.',
      );
    }
    if (error?.status === 403) {
      return tr('pro_manager_access.errors.forbidden', "Vous n'avez pas le droit d'effectuer cette action.");
    }
    return error?.data?.detail || tr('pro_manager_access.errors.generic', 'Une erreur est survenue. Vérifiez les informations puis réessayez.');
  };

  const openCreate = () => {
    setEditingManager(null);
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (manager) => {
    setEditingManager(manager);
    setFormError('');
    setFormOpen(true);
  };

  const submitForm = async (payload) => {
    setFormError('');
    try {
      if (editingManager) await updateManager({ userId: editingManager.id, ...payload }).unwrap();
      else await createManager(payload).unwrap();
      setFormOpen(false);
      setPageMessage({ severity: 'success', text: tr('pro_manager_access.saved', 'Les accès ont été enregistrés.') });
    } catch (error) {
      setFormError(friendlyError(error));
    }
  };

  const performConfirmedAction = async () => {
    if (!confirmAction) return;
    const { type, manager } = confirmAction;
    try {
      if (type === 'suspend') {
        await updateManager({
          userId: manager.id,
          grants: manager.grants.map((grant) => grantPayload(grant, false)),
        }).unwrap();
        setPageMessage({ severity: 'success', text: tr('pro_manager_access.suspended_success', 'Les accès ont été suspendus.') });
      } else {
        await removeManager({
          userId: manager.id,
          salon_ids: manager.grants.map((grant) => Number(grant.salon_id)),
        }).unwrap();
        setPageMessage({ severity: 'success', text: tr('pro_manager_access.removed_success', 'Les accès ont été retirés.') });
      }
      setConfirmAction(null);
    } catch (error) {
      setPageMessage({ severity: 'error', text: friendlyError(error) });
      setConfirmAction(null);
    }
  };

  if (accessLoading || managersQuery.isLoading || catalogQuery.isLoading) {
    return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}><CircularProgress aria-label={tr('common.loading', 'Chargement...')} /></Box>;
  }

  if (hasNoAuthorizedSalon) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5">{tr('pro_manager_access.access_suspended', 'Accès suspendu')}</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>{tr('pro_manager_access.no_authorized_salon', 'Aucun salon autorisé')}</Typography>
      </Paper>
    );
  }

  if (managersQuery.isError || catalogQuery.isError) {
    return <Alert severity="error">{tr('pro_manager_access.load_error', 'Impossible de charger les gestionnaires. Réessayez dans un instant.')}</Alert>;
  }

  return (
    <Box
      sx={{
        maxWidth: 1400,
        mx: 'auto',
        p: { xs: 1.5, sm: 2.5 },
        borderRadius: 3,
        background: 'linear-gradient(180deg, rgba(247, 236, 228, 0.72) 0%, rgba(255, 255, 255, 0) 260px)',
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography component="h1" variant="h4" sx={{ color: 'primary.900', fontWeight: 800 }}>{tr('pro_manager_access.title', 'Gestionnaires et accès')}</Typography>
          <Typography color="text.secondary">{tr('pro_manager_access.subtitle', 'Choisissez simplement qui peut faire quoi dans chaque salon.')}</Typography>
        </Box>
        {canManage && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>{tr('pro_manager_access.add', 'Ajouter une personne')}</Button>}
      </Stack>

      {pageMessage && <Alert severity={pageMessage.severity} onClose={() => setPageMessage(null)} sx={{ mb: 2 }}>{pageMessage.text}</Alert>}

      {!managers.length ? (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6">{tr('pro_manager_access.empty_title', 'Aucun gestionnaire pour le moment')}</Typography>
          <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>{tr('pro_manager_access.empty_help', 'Ajoutez une personne puis choisissez ses salons et ses droits.')}</Typography>
          {canManage && <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>{tr('pro_manager_access.add', 'Ajouter une personne')}</Button>}
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: 'primary.200', boxShadow: '0 12px 32px rgba(140, 80, 42, 0.08)' }}>
          <Table aria-label={tr('pro_manager_access.table_label', 'Liste des gestionnaires')}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.50', '& th': { color: 'primary.900', fontWeight: 700 } }}>
                <TableCell>{tr('pro_manager_access.columns.person', 'Personne')}</TableCell>
                <TableCell>{tr('pro_manager_access.columns.role', 'Rôle')}</TableCell>
                <TableCell>{tr('pro_manager_access.columns.salons', 'Salons autorisés')}</TableCell>
                <TableCell>{tr('pro_manager_access.columns.rights', 'Droits')}</TableCell>
                <TableCell>{tr('pro_manager_access.columns.status', 'État')}</TableCell>
                <TableCell align="right">{tr('pro_manager_access.columns.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {managers.map((manager) => {
                const active = manager.grants.some((grant) => grant.is_active !== false);
                const allCodes = [...new Set(manager.grants.flatMap((grant) => grant.permission_codes || []))];
                const hasFullAccess = manager.grants.some((grant) => grant.is_full_access);
                return (
                  <TableRow key={manager.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{[manager.first_name, manager.last_name].filter(Boolean).join(' ') || manager.email}</Typography>
                      <Typography variant="body2" color="text.secondary">{manager.email}</Typography>
                    </TableCell>
                    <TableCell>{tr(`pro_manager_access.roles.${manager.role}`, manager.role === 'professional_manager_employee' ? 'Gestionnaire + employé' : 'Gestionnaire')}</TableCell>
                    <TableCell>
                      <Stack direction="row" gap={0.5} flexWrap="wrap">{manager.grants.map((grant) => <Chip key={grant.salon_id} size="small" label={grant.name} variant="outlined" />)}</Stack>
                    </TableCell>
                    <TableCell>
                      {hasFullAccess
                        ? <Chip size="small" color="primary" label={tr('pro_manager_access.full_access', 'Gestion totale')} />
                        : <Tooltip title={allCodes.map((code) => permissionLabels[code] || code).join(', ')}><Typography variant="body2">{tr('pro_manager_access.rights_count', `${allCodes.length} droit(s)`, { count: allCodes.length })}</Typography></Tooltip>}
                    </TableCell>
                    <TableCell><Chip size="small" color={active ? 'success' : 'default'} label={active ? tr('pro_manager_access.status.active', 'Actif') : tr('pro_manager_access.status.suspended', 'Suspendu')} /></TableCell>
                    <TableCell align="right">
                      {canManage && (
                        <>
                          <Tooltip title={tr('pro_manager_access.actions.edit', 'Modifier')}><IconButton aria-label={tr('pro_manager_access.actions.edit', 'Modifier')} onClick={() => openEdit(manager)}><EditOutlinedIcon /></IconButton></Tooltip>
                          {active && <Tooltip title={tr('pro_manager_access.actions.suspend', 'Suspendre')}><IconButton aria-label={tr('pro_manager_access.actions.suspend', 'Suspendre')} color="warning" onClick={() => setConfirmAction({ type: 'suspend', manager })}><PauseCircleOutlineIcon /></IconButton></Tooltip>}
                          <Tooltip title={tr('pro_manager_access.actions.remove', 'Retirer')}><IconButton aria-label={tr('pro_manager_access.actions.remove', 'Retirer')} color="error" onClick={() => setConfirmAction({ type: 'remove', manager })}><DeleteOutlineIcon /></IconButton></Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ManagerAccessForm
        open={formOpen}
        onClose={() => !isBusy && setFormOpen(false)}
        onSubmit={submitForm}
        manager={editingManager}
        salons={salons}
        eligibleEmployees={eligibleEmployees}
        groups={groups}
        canGrantManagerManage={canGrantManagerManage}
        isSaving={createState.isLoading || updateState.isLoading}
        error={formError}
      />

      <Dialog open={Boolean(confirmAction)} onClose={() => !isBusy && setConfirmAction(null)}>
        <DialogTitle>{confirmAction?.type === 'suspend' ? tr('pro_manager_access.confirm.suspend_title', 'Suspendre ces accès ?') : tr('pro_manager_access.confirm.remove_title', 'Retirer ces accès ?')}</DialogTitle>
        <DialogContent>
          <Typography>{confirmAction?.type === 'suspend' ? tr('pro_manager_access.confirm.suspend_text', 'La personne ne pourra plus utiliser les salons sélectionnés jusqu’à une nouvelle modification.') : tr('pro_manager_access.confirm.remove_text', 'Les accès seront retirés, mais leur historique sera conservé.')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)} disabled={isBusy}>{tr('common.cancel', 'Annuler')}</Button>
          <Button variant="contained" color={confirmAction?.type === 'remove' ? 'error' : 'warning'} onClick={performConfirmedAction} disabled={isBusy}>
            {isBusy ? <CircularProgress size={22} /> : (confirmAction?.type === 'suspend' ? tr('pro_manager_access.actions.suspend', 'Suspendre') : tr('pro_manager_access.actions.remove', 'Retirer'))}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ManagerAccessPage = () => {
  const parentTheme = useTheme();
  const accessTheme = useMemo(
    () => createSalooneAccessTheme(parentTheme.direction),
    [parentTheme.direction],
  );

  return (
    <ThemeProvider theme={accessTheme}>
      <ManagerAccessPageContent />
    </ThemeProvider>
  );
};

export default ManagerAccessPage;
